import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import { FileDown, Sparkles } from 'lucide-react'
import { api } from '../../api/client'
import { useSession } from '../../store/user.store'
import { LoadingDots } from '../../components/ui/LoadingDots'
import { ErrorState } from '../../components/ui/ErrorState'
import { Button } from '../../components/ui/Button'
import { ShareButton } from '../../components/ui/ShareButton'
import { PageHeader } from '../../components/ui/PageHeader'
import { SectionedMarkdown } from '../../components/ui/SectionedMarkdown'
import { useFetchNatalPdf, useSendNatalPdfToChat } from '../../hooks/use-natal-pdf'

export function AstroNatalFullRoute() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = useSession((s) => s.user)
  const userName = user?.name
  const shareTextKey =
    user?.gender === 'female'
      ? 'natal.share_text_female'
      : user?.gender === 'male'
        ? 'natal.share_text_male'
        : 'natal.share_text_neutral'
  const sendToChat = useSendNatalPdfToChat()
  const fetchPdf = useFetchNatalPdf()

  const handleSendToChat = () => {
    sendToChat.mutate(undefined, {
      onSuccess: (res) => {
        if (res.delivered) {
          toast.success(t('natal.send_pdf_success'))
        } else {
          toast.error(t('natal.send_pdf_failed'))
        }
      },
      onError: (err) => {
        toast.error((err as Error).message || t('natal.send_pdf_failed'))
      },
    })
  }

  const query = useQuery({
    queryKey: ['natal', 'teaser'],
    queryFn: () => api.natalTeaser(),
    staleTime: 1000 * 60 * 60,
  })

  if (query.isLoading) return <LoadingDots compact />
  if (query.isError) return <ErrorState onRetry={() => query.refetch()} />

  const data = query.data
  if (!data || data.tier !== 'full' || !data.fullInterpretation) {
    return (
      <div className="flex flex-col items-center gap-4 py-10 text-center text-sage-700">
        <p>{t('natal.no_birth')}</p>
        <Button onClick={() => navigate('/astro/natal')}>{t('natal.back_to_teaser')}</Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5 pb-6">
      <PageHeader
        title={t('natal.full_title')}
        subtitle={userName ?? undefined}
        backTo="/astro/natal"
      />

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          size="lg"
          onClick={handleSendToChat}
          loading={sendToChat.isPending}
          leftIcon={<FileDown className="h-4 w-4" strokeWidth={1.8} aria-hidden />}
        >
          {t('natal.send_pdf_to_chat')}
        </Button>
        <ShareButton size="lg" text={t(shareTextKey)} getFile={fetchPdf} />
      </div>

      <SectionedMarkdown>{data.fullInterpretation}</SectionedMarkdown>
    </div>
  )
}
